'use strict';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import 'regenerator-runtime/runtime';

{
	let map;
	let activeMarker = null;
	let markers = [];
	let clusterer = null;
	let searchLocationLat = null;
	let searchLocationLng = null;
	let searchExecuted = false; // Variabile di stato per tracciare l'esecuzione della ricerca
	let triggerExecuted = false; // Variabile di stato per tracciare l'esecuzione della trigger
	let infoWindow = null;
	let stores = []; // Variabile globale per memorizzare i dati degli store
	let skipGA4Event = false;
	let searchTimeout = null;




	function loadGoogleMapsAPI() {
		if (!window.storeLocatorSettings.apiKey && !IS_DEV) {
			throw (`Missing Google Map Api Key`);
		}

		if (window.googlePromise) {
			return window.googlePromise.then(() => {
				if (window.google && window.google.maps && window.google.maps.Map) {
					return;
				} else {
					throw new Error('Google Maps modules failed to load');
				}
			});
		}

		return new Promise((resolve, reject) => {
			// Previene caricamenti multipli
			if (window.google && window.google.maps) {
				resolve();
				return;
			}

			// Callback globale che verrà chiamato quando Google Maps è pronto
			window.initMapCallback = function() {
				// Assicurati che i moduli interni siano caricati
				if (window.google && window.google.maps && window.google.maps.Map) {
					resolve();
				} else {
					// Se i moduli non sono ancora pronti, riprova dopo un breve delay
					setTimeout(() => {
						if (window.google && window.google.maps && window.google.maps.Map) {
							resolve();
						} else {
							reject(new Error('Google Maps modules failed to load'));
						}
					}, 100);
				}
			};

			const script = document.createElement('script');
			const locale = detectLocale();
			script.src = `https://maps.googleapis.com/maps/api/js?key=` + window.storeLocatorSettings.apiKey + `&callback=initMapCallback&libraries=places&region=EN&language=en&v=weekly&loading=async`;
			script.async = true;
			script.onerror = reject;
			script.setAttribute('data-cookieconsent', 'necessary');

			// Aggiungi nonce se presente per CSP
			const metaNonce = document.querySelector('meta[property="csp-nonce"]');
			if (metaNonce) {
				script.nonce = metaNonce.content;
			}

			document.head.appendChild(script);
		});
	}

	async function loadMapStyle() {
		try {
			const response = await fetch('/data/mapStyle.json');
			return await response.json();
		} catch (error) {
			// console.error('Error loading map style:', error);
			return [];
		}
	}

	function displayInfoWindow(marker, store) {
		// Chiudi eventuali finestre di informazioni aperte
		if (infoWindow) {
			infoWindow.close();
		}

		// Calcola la distanza, se non è già stata calcolata
		if (typeof store.distance === 'undefined') {
			store.distance = calculateDistance(searchLocationLat, searchLocationLng, store.lat, store.lng);
		}

		let hoursHtml = '';
		const storeStatus = isStoreOpen(store);

		// Ottieni il giorno corrente in italiano come abbreviazione (es. "Lun" per "Lunedì")
		const todayAbbreviation = new Date().toLocaleDateString('it-IT', { weekday: 'short' }).toLowerCase();

		if (store.hours) {
			store.hours.forEach(day => {
				const dayName = day.day.trim().toLowerCase();
				// Aggiunge la classe .highlight-today al giorno corrente
				const isToday = dayName === todayAbbreviation ? 'highlight-today' : '';

				if (day.orari.length === 1 && day.orari[0].trim() === 'Chiuso') {
					hoursHtml += `<li class="${isToday}"><div class="giorno">${dayName}:</div><div class="orari">${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_CHIUSO}</div></li>`;
				} else {
					hoursHtml += `<li class="${isToday}"><div class="giorno">${dayName}</div><div class="orari">${day.orari.join(' / ')}</div></li>`;
				}
			});
		}

		const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${marker.getPosition().lat()},${marker.getPosition().lng()}`;

		let nomeSt = store.name;

		if (!nomeSt) { nomeSt = "-" }
		else {
			nomeSt = nomeSt.replaceAll(" ", "_").toLowerCase() + "_";
			nomeSt = nomeSt.replaceAll("_-_", "_");
		}

		nomeSt = nomeSt.replace(/'/g, "`");

		if (window.data.provinciaGA4 && window.data.provinciaGA4 != "" && !window.data.searchByTextBox) {
			nomeSt = nomeSt + window.data.provinciaGA4;
		} else {
			nomeSt += "-";
		}

		store.nomeContenuto = nomeSt;

		// Crea un nuovo contenuto HTML per l'infoWindow
		const contentString = `
         <div class="infoWindowContent">
            <div class="infoHeader">
                <div class="wrap">
                    <div class="storeName">${store.name}</div>
                    <div class="storeDistance">${store.distance.toFixed(2)} km</div>
                </div>
            </div>
            <div class="infoBody">
                <div class="storeAddress">${store.address}, ${store.city}</div>
                ${hoursHtml ? `
                <div class="storeHours">
                    <div class="hoursTitle">Orari</div>
                    <div class="today">
                        <div role="button" tabindex="0" class="wrap-today" onclick="storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.agenteRif}','${store.countryExt}','tel_map')">
                            <svg class="iconHours" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="7.9998" cy="8.00065" r="6.66667" stroke="#2B3F6C"></circle>
                              <path d="M7.99976 5.33398V7.82226C7.99976 7.93371 8.05546 8.03778 8.14819 8.09961L9.99976 9.33398" stroke="#2B3F6C" stroke-linecap="round"></path>
                            </svg>
                            <span>
                                <strong>${storeStatus.status}</strong>
                                ${storeStatus.status === `${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_APERTO}` ? ` · ${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_CHIUDE_A} ${storeStatus.closesAt}` : storeStatus.nextOpen ? ` · ${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_APRE_A} ${storeStatus.nextOpen}` : ''}
                            </span>
                            <svg class="iconArrow" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" onclick="storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.agenteRif}','${store.countryExt}','orari_map')">
                              <rect width="20" height="20" fill="#F0F3F7" />
                              <path d="M6 8L10 12L14 8" stroke="#232120" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </div>
                        <ul class="week">
                            ${hoursHtml}
                        </ul>
                    </div>
                </div>` : ''}
                ${store.phone ? `
                <div class="storePhone">
                    <div class="phoneTitle">Telefono</div>
                    <div class="phoneNumber">
                      <svg class="iconPhone" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.708 12.4789C10.4052 13.7817 7.06855 12.5575 4.25551 9.74449C1.44248 6.93145 0.218261 3.59485 1.52114 2.29197L2.37898 1.43412C2.9712 0.841904 3.94703 0.857558 4.55856 1.46909L5.88728 2.79781C6.49881 3.40934 6.51447 4.38517 5.92225 4.97739L5.73799 5.16165C5.41825 5.48139 5.38696 5.99721 5.68407 6.35725C5.97064 6.70452 6.2796 7.05043 6.61458 7.38542C6.94957 7.7204 7.29548 8.02936 7.64275 8.31593C8.00279 8.61304 8.51861 8.58175 8.83835 8.26201L9.02261 8.07775C9.61483 7.48553 10.5907 7.50119 11.2022 8.11272L12.5309 9.44144C13.1424 10.053 13.1581 11.0288 12.5659 11.621L11.708 12.4789Z" stroke="#2B3F6C"></path>
                      </svg>
                      <a href="tel:${store.phone}"
							class="dtEventSend"
							data-gtm-eventname="store_locator_interactions"
							data-gtm-position="mappa"
							data-gtm-label="get_call"
							data-gtm-nomecontenuto="${store.nomeContenuto}" onclick="storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.countryExt}','${store.agenteRif}','tel_map')"
					  >${store.phone}</a>
                    </div>
                </div>
                ` : ''}
            </div>
            <div class="infoFooter">
                <a class="btn btn--outline dtEventSend" data-gtm-eventname="store_locator_interactions"  onclick="storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.countryExt}','${store.agenteRif}','direction_map')" data-gtm-position="mappa" data-gtm-label="get_direction" data-gtm-nomecontenuto="${store.nomeContenuto}" href="${directionsUrl}" target="_blank" class="btn btn--outline">Ottieni indicazioni</a>
            </div>
        </div>
    `;

		// Crea una nuova finestra di informazioni
		infoWindow = new google.maps.InfoWindow({
			content: contentString,
		});

		// Aggiungi un listener per chiudere la finestra al click sul marker
		google.maps.event.addListener(marker, 'click', () => {
			infoWindow.open(map, marker);
		});

		// Listener per attivare toggleWeekActive sulla .wrap-today dentro l'infoWindow
		google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
			const wrapTodayInInfoWindow = document.querySelector('.infoWindowContent .wrap-today');
			if (wrapTodayInInfoWindow) {
				wrapTodayInInfoWindow.addEventListener('click', toggleWeekActive);
				wrapTodayInInfoWindow.addEventListener('keydown', (e) => {
					if (e.key === 'Enter' || e.keyCode === 13) {
						e.preventDefault();
						toggleWeekActive(e);
					}
				});
			}
		});
	}

	function addMarkerClickListener(marker, storeId, storeName) {
		const store = stores.find(s => s.id === storeId);
		if (!store) return; // Se `store` non viene trovato, esci dalla funzione

		marker.addListener('click', () => {
			// Scrolla fino alla posizione dell'elemento nel `listStore`
			const storeElement = document.getElementById(`store-${storeId}`);
			if (storeElement) {
				storeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
				document.querySelectorAll('.listStore li').forEach(el => el.classList.remove('active'));
				storeElement.classList.add('active');
				changeMarkerIcon(marker);
				map.setCenter(marker.getPosition());

				// Calcola e aggiorna la distanza per il `store` ogni volta che si clicca sul marker
				store.distance = calculateDistance(searchLocationLat, searchLocationLng, store.lat, store.lng);

				// Apri l'infoWindow sul marker
				displayInfoWindow(marker, store);
				infoWindow.open(map, marker); // Assicurati che la finestra di informazioni sia visualizzata

				// Tracciamento interazione GA4
				const prov = window.data.provinciaGA4;
				const nomeContenuto = prov && !window.data.searchByTextBox ? `${storeName}_${prov}` : `${storeName}_-`;
				storeLocatorInteractions("mappa", "seleziona_store", nomeContenuto);
				storeSelezionato(store.id, store.name4GA, store.indirizzo4GA, store.type, store.citta4GA,store.agenteRif, store.countryExt, 'click_map');

				const btnTop = document.querySelector('.btn-top');
				if (btnTop) {
					btnTop.classList.add('visible');
				}
			}
		});
	}

	function changeMarkerIcon(marker) {
		if (activeMarker) {
			activeMarker.setIcon({
				url: '/img/pin.png',
			});
		}
		marker.setIcon({
			url: '/img/pin-selected.png',
		});
		activeMarker = marker;
	}

	function clearMarkers() {
		if (clusterer) {
			clusterer.clearMarkers();
		}
		markers.forEach(marker => marker.setMap(null));
		markers = [];
	}

	async function fetchStoreData(map) {
		try {
			const response = await fetch('/api/store-locator/all');
			stores = await response.json(); // Aggiorna la variabile globale `stores`
			const locale = detectLocale();
			const { country } = getCountryAndCenter(locale);

			const filteredStores = stores.filter(store => store.country === country);
			// Calcola la distanza per ogni store rispetto alla posizione di ricerca attuale
			filteredStores.forEach(store => {
				store.distance = calculateDistance(searchLocationLat, searchLocationLng, store.lat, store.lng);
			});
			const customIcon = {
				url: '/img/pin.png',
			};

			markers = filteredStores.map((store) => {
				const position = { lat: store.lat, lng: store.lng };

				if (!isNaN(position.lat) && !isNaN(position.lng)) {
					const marker = new google.maps.Marker({
						position: position,
						title: store.name,
						icon: customIcon,
						id: `store-${store.id}`,
						map: map
					});

					addMarkerClickListener(marker, store.id, store.name);
					return marker;
				} else {
					return null;
				}
			}).filter(marker => marker !== null);

			const renderer = {
				render({ count, position }) {
					const svg = window.btoa(`
						<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
						<rect width="48" height="48" rx="24" fill="#000000"/>
						</svg>
					`);
					return new google.maps.Marker({
						position,
						icon: {
							url: `data:image/svg+xml;base64,${svg}`,
							anchor: new google.maps.Point(24, 24),
						},
						label: {
							text: String(count),
							color: "white",
							fontSize: "12px",
						},
						zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
					});
				},
			};

			if (clusterer) {
				clusterer.clearMarkers();
			}

			clusterer = new MarkerClusterer({
				map: map,
				markers: markers,
				renderer: renderer
			});

			return markers;

		} catch (error) {
			console.error('Error fetching store data:', error);
		}
	}

	async function fetchAgentsData(tipo, countrycode, province, city, areac, shortn) {
		try {
			// console.log(`Fetching agents data with params: tipo=${tipo}, countrycode=${countrycode}, provcode=${province}, citycode=${city}, areacode=${areac}, shortname=${shortn}`);
			const response = await fetch(`/api/store-locator/agenti-by-coords?tipo=${tipo}&countrycode=${countrycode}&provcode=${province}&citycode=${city}&areacode=${areac}&shortname=${shortn}`);
			const data = await response.json();

			data.forEach(agent => agent.nameWebRanking += "_agente_di_zona");

			if (window.data.provinciaGA4 && !window.data.searchByTextBox)
				data.forEach(agent => agent.nameWebRanking += "_" + window.data.provinciaGA4);

			//console.log('Agents data fetched:', data);
			return data;
		} catch (error) {
			console.error('Error fetching agent data:', error);
			return [];
		}
	}

	function calculateDistance(lat1, lng1, lat2, lng2) {
		const R = 6371;
		const dLat = (lat2 - lat1) * Math.PI / 180;
		const dLng = (lng2 - lng1) * Math.PI / 180;
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
			Math.sin(dLng / 2) * Math.sin(dLng / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c;
	}

	function isStoreOpen(store) {
		if (!store || !store.hours) {
			// console.error(`store.hours è null o undefined per il negozio:`, store);
			return { status: "Chiuso", closesAt: null, nextOpen: null };
		}

		const today = new Date();
		const daysMapAbbreviated = {
			'domenica': 'Dom',
			'lunedì': 'Lun',
			'martedì': 'Mar',
			'mercoledì': 'Merc', // Stiamo usando "Merc" perché è così che ti arriva nei dati
			'giovedì': 'Gio',
			'venerdì': 'Ven',
			'sabato': 'Sab'
		};

		// Ottieni il giorno corrente in italiano
		const dayInItalian = today.toLocaleString('it-IT', { weekday: 'long', timeZone: 'Europe/Rome' }).toLowerCase();
		const dayAbbreviation = daysMapAbbreviated[dayInItalian];

		if (!dayAbbreviation) {
			// console.warn(`Abbreviazione non trovata per il giorno corrente: ${dayInItalian}`);
			return { status: "Chiuso", closesAt: null, nextOpen: null };
		}

		const hoursData = store?.hours?.find(h => h.day.trim() === dayAbbreviation);

		if (!hoursData) {
			// console.warn(`Nessun orario trovato per il giorno: ${dayAbbreviation} per il negozio: ${store.name}`);
			return { status: "Chiuso", closesAt: null, nextOpen: getNextOpeningTime(store, today) };
		}


		if (!hoursData) {
			// console.warn(`Nessun orario trovato per il giorno: ${dayAbbreviation} per il negozio: ${store.name}`);
			return { status: "Chiuso", closesAt: null, nextOpen: null };
		}

		if (hoursData.orari.length === 1 && hoursData.orari[0].trim().toLowerCase() === 'chiuso') {
			return { status: "Chiuso", closesAt: null, nextOpen: getNextOpeningTime(store, today) };
		}

		const options = { timeZone: 'Europe/Rome', hour12: false, hour: '2-digit', minute: '2-digit' };
		const timeInRomeString = today.toLocaleTimeString('it-IT', options);
		const [currentHour, currentMinute] = timeInRomeString.split(':').map(Number);
		const nowInMinutes = currentHour * 60 + currentMinute;

		let nextOpenTime = null;

		for (let orario of hoursData.orari) {
			const [openTime, closeTime] = orario.split(/\s*[-–—]\s*/).map(time => time.trim());

			if (!openTime || !closeTime) continue;

			if (!/^\d{1,2}:\d{2}$/.test(openTime) || !/^\d{1,2}:\d{2}$/.test(closeTime)) continue;

			const [openHour, openMinute] = openTime.split(':').map(Number);
			const [closeHour, closeMinute] = closeTime.split(':').map(Number);

			if (isNaN(openHour) || isNaN(openMinute) || isNaN(closeHour) || isNaN(closeMinute)) continue;

			const openInMinutes = openHour * 60 + openMinute;
			const closeInMinutes = closeHour * 60 + closeMinute;

			if (nowInMinutes >= openInMinutes && nowInMinutes < closeInMinutes) {
				return {
					status: `${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_APERTO}`,
					closesAt: `${closeHour.toString().padStart(2, '0')}:${closeMinute.toString().padStart(2, '0')}`,
					nextOpen: null
				};
			}

			if (nowInMinutes < openInMinutes && (nextOpenTime === null || openInMinutes < nextOpenTime)) {
				nextOpenTime = openInMinutes;
			}
		}

		if (nextOpenTime !== null) {
			const nextOpenHour = Math.floor(nextOpenTime / 60);
			const nextOpenMinute = nextOpenTime % 60;
			return {
				status: `${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_CHIUSO}`,
				closesAt: null,
				nextOpen: `${nextOpenHour.toString().padStart(2, '0')}:${nextOpenMinute.toString().padStart(2, '0')}`
			};
		}

		// Se l'orario di riapertura non è oggi, controlla i giorni successivi
		const nextOpen = getNextOpeningTime(store, today);

		return {
			status: `${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_CHIUSO}`,
			closesAt: null,
			nextOpen: nextOpen
		};
	}

	function getNextOpeningTime(store, currentDate) {
		if (!store.hours || !Array.isArray(store.hours)) {
			// console.error('store.hours is undefined or not an array in getNextOpeningTime', store);
			return null;
		}
		const daysMapAbbreviated = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
		let dayIndex = currentDate.getDay(); // Ottiene l'indice del giorno della settimana (0 è Domenica, 6 è Sabato)

		for (let i = 1; i <= 7; i++) {
			dayIndex = (dayIndex + 1) % 7;
			const nextDayAbbreviation = daysMapAbbreviated[dayIndex];
			const nextDayData = store.hours.find(h => h.day.trim() === nextDayAbbreviation);

			if (nextDayData && nextDayData.orari.length > 0 && nextDayData.orari[0].trim().toLowerCase() !== 'chiuso') {
				const [nextOpenTime] = nextDayData.orari[0].split(/\s*[-–—]\s*/).map(time => time.trim());
				return `${nextDayAbbreviation} ${nextOpenTime}`;
			}
		}

		return null; // Non dovrebbe mai arrivare qui, ma lo mettiamo per sicurezza
	}

	function toggleWeekActive(event) {
		const wrapTodayElement = event.target.closest('.wrap-today');
		if (wrapTodayElement) {
			const todayElement = wrapTodayElement.closest('.today');
			if (todayElement) {
				const weekList = todayElement.querySelector('.week');
				if (weekList && weekList.classList.contains('week')) {
					weekList.classList.toggle('active');
					todayElement.classList.toggle('active');
				}
			} else {
				// console.log('Elemento .today non trovato.');
			}
		} else {
			// console.log('Elemento .wrap-today non trovato.');
		}
	}

	function detectLocale() {
		const metaTags = document.getElementsByTagName('meta');
		for (let i = 0; i < metaTags.length; i++) {
			if (metaTags[i].getAttribute('property') === 'og:locale') {
				return metaTags[i].getAttribute('content');
			}
		}
		return 'en_US';
	}

	function getCountryAndCenter(locale) {
		const mapSettings = {
			it_IT: { country: 'IT', center: { lat: 41.9028, lng: 12.4964 } },
			fr_FR: { country: 'FR', center: { lat: 48.8566, lng: 2.3522 } },
			de_DE: { country: 'DE', center: { lat: 52.5200, lng: 13.4050 } },
			de_AT: { country: 'AT', center: { lat: 48.2082, lng: 16.3738 } },
			nl_BE: { country: 'BE', center: { lat: 50.8503, lng: 4.3517 } },
			de_CH: { country: 'CH', center: { lat: 46.2044, lng: 6.1432 } },
			fr_CH: { country: 'CH', center: { lat: 46.2044, lng: 6.1432 } }
		};
		return mapSettings[locale] || { country: 'US', center: { lat: 37.7749, lng: -122.4194 } };
	}

	function getProvinceFromUrl() {
		const url = window.location.href;
		const match = url.match(/\/italia\/([^\/]+)/);
		return match ? match[1] : null;
	}

	async function initMap() {
		let autocomplete = null;
		const loader = document.querySelector('.loader');
		const wrapMap = document.querySelector('.wrap-map');
		loader.style.display = 'flex';
		wrapMap.classList.add('loading');

		try {
			const mapStyle = await loadMapStyle();
			const locale = detectLocale();
			const { country, center } = getCountryAndCenter(locale);

			map = new google.maps.Map(document.getElementById('map'), {
				zoom: 6,
				center: center,
				styles: mapStyle
			});

			const autocompleteInput = document.querySelectorAll('.search-input');

			// Verifica disponibilità Places API
			autocomplete = null;
			if (google.maps.places && google.maps.places.Autocomplete) {
				try {
					autocomplete = new google.maps.places.Autocomplete(autocompleteInput[0]);
				} catch (placesError) {
					console.warn('Places API Autocomplete could not be initialized:', placesError);
				}
			} else {
				console.warn('Places API not loaded. Autocomplete disabled.');
			}

			if (autocomplete) {
				// Evento per selezione del suggerimento con il mouse
				const observer = new MutationObserver(() => {
					const autocompleteContainer = document.querySelector('.pac-container');
					if (autocompleteContainer) {
						autocompleteContainer.addEventListener('click', async () => {

							if (!skipGA4Event) {
								console.log("589 Suggestion clicked. Triggering GA4 event.");
								//await triggerGA4Event();
								skipGA4Event = true;
							}

							const place = autocomplete.getPlace();
							if (place.geometry) {
								searchLocationLat = place.geometry.location.lat();
								searchLocationLng = place.geometry.location.lng();
								console.log("599 window.data.searchByTextBox:", window.data.searchByTextBox)
								await performSearch(place.geometry.location, [place]);
								skipGA4Event = false; // Reset flag al termine della ricerca
							}
						});
					}
				});
				observer.observe(document.body, { childList: true, subtree: true });

				// Evento per suggerimenti con Autocomplete
				google.maps.event.addListener(autocomplete, 'place_changed', async function () {
					if (searchTimeout) {
						clearTimeout(searchTimeout);
						searchTimeout = null;
					}

					if (!skipGA4Event) { // Controllo skipGA4Event
						console.log("614 Suggestion selected. Triggering GA4 event.");
						window.data.searchByTextBox = true;
						window.data.searchByCTA= false;
						//await triggerGA4Event();
						skipGA4Event = true;
					}
					const place = autocomplete.getPlace();
					if (place.geometry) {
						searchLocationLat = place.geometry.location.lat();
						searchLocationLng = place.geometry.location.lng();
						await performSearch(place.geometry.location, [place]);
						skipGA4Event = false; // Reset flag dopo ricerca
					}
				});
			}

			markers = await fetchStoreData(map);

			google.maps.event.addListenerOnce(map, 'idle', () => {
				loader.style.display = 'none';
				wrapMap.classList.remove('loading');
				wrapMap.classList.add('loaded');
			});
		} catch (error) {
			console.error('Error initializing map:', error);
			loader.style.display = 'none';
			wrapMap.classList.remove('loading');
			// Mostra messaggio di errore all'utente
			const errorMsg = document.createElement('div');
			errorMsg.className = 'map-error-message';
			errorMsg.style.cssText = 'padding: 20px; text-align: center; color: #721c24; background-color: #f8d7da; border: 1px solid #f5c6cb; border-radius: 4px; margin: 20px;';
			errorMsg.textContent = 'Unable to load map. Please refresh the page or try again later.';
			document.getElementById('map').appendChild(errorMsg);
		}


		async function performSearch(location, results) {
			// console.log("Attempting search. Search executed:", searchExecuted);
			if (searchExecuted) return; // Evita di eseguire nuovamente la ricerca se già eseguita

			searchExecuted = true; // Imposta lo stato per indicare che la ricerca è in corso
			// console.log("Search started. Search executed set to true.");


			try {
				// console.log(`Performing search with Lat: ${searchLocationLat}, Lng: ${searchLocationLng}`);

				const wrapResults = document.querySelector('.wrap-results');
				const formTop = document.querySelector('.section--form-top-store-locator');
				const formBottom = document.querySelector('.section--form-bottom-store-locator');
				const listStore = document.querySelector('.listStore');
				const listAgent = document.querySelector('.listAgent');
				const storeFilters = document.querySelector('.storeFilters');
				const noStore = document.querySelector('.no-store');
				const btnTop = document.querySelector('.btn-top');
				const mapOverlay = document.querySelector('#map-overlay');

				map.setCenter(location);
				const textOverlay = document.querySelector('.text-overlay');
				if (textOverlay) {
					textOverlay.remove();
				}
				if (wrapResults) {
					wrapResults.classList.add('active');
				}
				if (wrapMap) {
					wrapMap.classList.add('searched');
				}
				if (formTop) {
					formTop.classList.add('searched');
				}
				if (formBottom) {
					formBottom.classList.add('searched');
				}
				if (btnTop) {
					btnTop.classList.add('searched');
				}
				if (mapOverlay) {
					mapOverlay.classList.remove('active');
				}

				loader.style.display = 'flex';

				let storesInRadius = [];
				let stores = [];

				let tipo = ''; // Dichiara 'tipo' qui per renderlo accessibile

				try {
					// console.log('fetching stores by coordinates');
					if (results && results[0] && results[0].address_components) {

						const ordinatiS = {
							"country": 1,
							"administrative_area_level_1": 2,
							"administrative_area_level_2": 3,
							"administrative_area_level_3": 4,
							"administrative_area_level_4": 5,
							"administrative_area_level_5": 6,
							"locality": 7,
							"route": 8,
							"postal_code": 9,
							"street_address": 10
						};
						const addressComponents = results[0].address_components;
						const country = addressComponents.find(component => component.types.includes("country"))?.short_name || 'N/A';
						const province = addressComponents.find(component => component.types.includes("administrative_area_level_2"))?.short_name || 'N/A';
						const city = addressComponents.find(component => component.types.includes("administrative_area_level_3"))?.short_name || 'N/A';
						const areac = addressComponents.find(component => component.types.includes("administrative_area_level_1"))?.short_name || 'N/A';

						var shortn = '';
						for (var i = addressComponents.length - 1; i >= 0; i--) {
							var componente = addressComponents[i];

							for (var j = 0; j < componente.types.length; j++) {
								if (ordinatiS[componente.types[j]]) {
									tipo = componente.types[j];
									shortn = componente.short_name;
									break;
								}
							}
						}
						// console.log('tipo ', tipo, '- short 1', shortn, '- country 1', country);

						let response = await fetch(`/api/store-locator/by-coords?lat=${location.lat()}&lng=${location.lng()}&tipo=${tipo}&countrycode=${country}&provcode=${province}&citycode=${city}&areacode=${areac}&shortname=${shortn}`);
						stores = await response.json();



					} else {
						//console.error('No results[0] or address_components in performSearch');
						// alert('Non è stato possibile trovare l\'indirizzo inserito. Per favore, verifica e riprova.');
						return;
					}
				} catch (error) {
					console.error('Error fetching store data:', error);
				}

				storesInRadius = stores.map((store) => {
					const distance = calculateDistance(location.lat(), location.lng(), store.lat, store.lng);
					return { ...store, distance };
				});

				storesInRadius.sort((a, b) => {
					const order = { 'store': 1, 'showroom': 2, 'agent': 3 };
					const typeA = a.type.toLowerCase();
					const typeB = b.type.toLowerCase();
					return order[typeA] - order[typeB] || a.distance - b.distance;
				});

				// Limita i risultati a 10 se la ricerca è per indirizzo o CAP
				if (tipo === 'street_address' || tipo === 'postal_code') {
					storesInRadius = storesInRadius.slice(0, 10);
					// console.log('Limited to 10 results for street_address or postal_code');
				}

				clearMarkers();

				markers = storesInRadius.map((store) => {
					const marker = new google.maps.Marker({
						position: { lat: store.lat, lng: store.lng },
						map: map,
						title: store.name,
						icon: {
							url: '/img/pin.png',
						},
						id: `store-${store.id}`
					});

					addMarkerClickListener(marker, store.id, store.name);

					return marker;
				});

				// Rimuovo i marker dal clusterer precedente e aggiorno con i nuovi
				if (clusterer) {
					clusterer.clearMarkers();
				}

				// Ricrea il clusterer con i marker aggiornati
				clusterer = new MarkerClusterer({
					map: map,
					markers: markers,  // Aggiungi i nuovi marker
					renderer: {
						render({ count, position }) {
							const svg = window.btoa(`
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="48" height="48" rx="24" fill="#000000"/>
                        </svg>
                    `);
							return new google.maps.Marker({
								position,
								icon: {
									url: `data:image/svg+xml;base64,${svg}`,
									anchor: new google.maps.Point(24, 24),
								},
								label: {
									text: String(count),
									color: "white",
									fontSize: "12px",
								},
								zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
							});
						},
					}
				});

				listStore.innerHTML = '';
				listAgent.innerHTML = '';

				storesInRadius.forEach((store) => {
					const storeStatus = isStoreOpen(store);
					store.storeStatus = storeStatus;

					const today = new Date();
					const dayInItalian = today.toLocaleDateString('it-IT', { weekday: 'long' }).toLowerCase();

					// Mappatura per abbreviare i giorni della settimana
					const daysMap = {
						'domenica': 'Dom',
						'lunedì': 'Lun',
						'martedì': 'Mar',
						'mercoledì': 'Merc',
						'giovedì': 'Gio',
						'venerdì': 'Ven',
						'sabato': 'Sab'
					};

					const todayAbbreviated = daysMap[dayInItalian] || dayInItalian;

					let hoursHtml = '';
					if (store.hours) {
						store.hours.forEach(day => {
							const dayAbbreviated = daysMap[day.day.trim().toLowerCase()] || day.day.trim();
							const isToday = dayAbbreviated === todayAbbreviated ? 'highlight-today' : '';

							if (day.orari.length === 1 && day.orari[0].trim() === 'Chiuso') {
								hoursHtml += `<li class="${isToday}"><div class="giorno">${dayAbbreviated}:</div><div class="orari">${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_CHIUSO}</div></li>`;
							} else {
								hoursHtml += `<li class="${isToday}">
                            <div class="giorno">${dayAbbreviated}</div>
                            <div class="orari">${day.orari.join(' / ')}</div>
                        </li>`;
							}
						});
					}

					const storeItem = document.createElement('li');
					storeItem.id = `store-${store.id}`;
					storeItem.dataset.markerId = `store-${store.id}`;

					var nomeContenuto = "";
					var prov = window.data.provinciaGA4;

					if (prov && !window.data.searchByTextBox)
						nomeContenuto = store.name.replace(/'/g, "`") + "_" + prov;
					else
						nomeContenuto = store.name.replace(/'/g, "`") + "_-";

					const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;


					storeItem.innerHTML = `
						<div class="store-header" onclick="storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.agenteRif}','${store.countryExt}','click_card')">
							<div class="wrap">
								<div class="storeName">${store.name}</div>
								<div class="storeDistance">${store.distance.toFixed(2)} km</div>
							</div>
						</div>
						<div class="store-body" onclick="storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.agenteRif}','${store.countryExt}','click_card')">
							<div class="wrap">
								<div class="item-info">
									<ul>
										<li>
											<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M13.333 7.85102C13.333 11.4696 9.06638 15.2399 7.99972 15.2399C6.93305 15.2399 2.66638 11.4696 2.66638 7.85102C2.66638 4.93618 5.0542 2.57324 7.99972 2.57324C10.9452 2.57324 13.333 4.93618 13.333 7.85102Z" stroke="#2B3F6C"/>
												<circle cx="2" cy="2" r="2" transform="matrix(-1 0 0 1 9.99976 5.57324)" stroke="#2B3F6C"/>
											</svg>
											<span>${store.address} - ${store.city}</span>
										</li>
										${hoursHtml ? `
										<li class="hours">
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<circle cx="7.9998" cy="8.00065" r="6.66667" stroke="#2B3F6C" />
												<path d="M7.99976 5.33398V7.82226C7.99976 7.93371 8.05546 8.03778 8.14819 8.09961L9.99976 9.33398" stroke="#2B3F6C" stroke-linecap="round" />
											</svg>
											<div class="today">
												<div role="button" tabindex="0" class="wrap-today" onclick="storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.agenteRif}','${store.countryExt}','orari_card')">
													<span>
														<strong>${storeStatus.status}</strong>
														${storeStatus.status === `${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_APERTO}` ? ` · ${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_CHIUDE_A} ${storeStatus.closesAt}` : storeStatus.nextOpen ? ` · ${window.storeLocatorSettings.localizedStrings.LBL_ORARIO_APRE_A} ${storeStatus.nextOpen}` : ''}
													</span>
													<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
														<rect width="20" height="20" fill="#DEDEDE" />
														<path d="M6 8L10 12L14 8" stroke="#232120" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
													</svg>
												</div>
												<ul class="week">
													${hoursHtml}
												</ul>
											</div>
										</li>` : ''}
										${store.phone ? `
										<li>
											<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M11.708 12.4789C10.4052 13.7817 7.06855 12.5575 4.25551 9.74449C1.44248 6.93145 0.218261 3.59485 1.52114 2.29197L2.37898 1.43412C2.9712 0.841904 3.94703 0.857558 4.55856 1.46909L5.88728 2.79781C6.49881 3.40934 6.51447 4.38517 5.92225 4.97739L5.73799 5.16165C5.41825 5.48139 5.38696 5.99721 5.68407 6.35725C5.97064 6.70452 6.2796 7.05043 6.61458 7.38542C6.94957 7.7204 7.29548 8.02936 7.64275 8.31593C8.00279 8.61304 8.51861 8.58175 8.83835 8.26201L9.02261 8.07775C9.61483 7.48553 10.5907 7.50119 11.2022 8.11272L12.5309 9.44144C13.1424 10.053 13.1581 11.0288 12.5659 11.621L11.708 12.4789Z" stroke="#2B3F6C"/>
											</svg>
											<a onclick="storeLocatorInteractions('listing', 'get_call', '${nomeContenuto}');storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.agenteRif}','${store.countryExt}','tel_card')" href="tel:${store.phone}">${store.phone}</a>
										</li>` : ''}
									</ul>
								</div>
								<div class="item-cta">
									<div>
										<a target="_blank" onclick="storeLocatorInteractions('listing', 'get_direction', '${nomeContenuto}');storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.agenteRif}','${store.countryExt}','direction_card')" href="${directionsUrl}" class="btn btn--outline" data-store-name="${store.name4GA}" data-store-provincia="${store.descrizioneProvincia}" data-lat="${store.lat}" data-lng="${store.lng}">${window.storeLocatorSettings.localizedStrings.LBL_RAGGIUNGERE_STORE}</a>
									</div>
									<div>
										<a href="javascript:;" onclick="storeLocatorInteractions('listing', 'contact_store', '${nomeContenuto}');storeSelezionato(${store.id},'${store.name4GA}','${store.indirizzo4GA}','${store.type}','${store.citta4GA}','${store.agenteRif}','${store.countryExt}','contact_card')" class="btn btn--brown linkFancyStore" data-store="${encodeURIComponent(JSON.stringify(store))}">${window.storeLocatorSettings.localizedStrings.LBL_CONTATTA_STORE}</a>
									</div>
								</div>
							</div>
						</div>
					`;


					storeItem.addEventListener('click', () => {
						const markerId = storeItem.dataset.markerId;
						const marker = markers.find(m => m.id === markerId);

						if (marker) {
							displayInfoWindow(marker, store); // Apre l'infoWindow sul marker corrispondente
							infoWindow.open(map, marker); // Mostra l'infoWindow
							map.setCenter(marker.getPosition()); // Centra la mappa sul marker

							document.querySelectorAll('.listStore li').forEach(el => el.classList.remove('active'));
							storeItem.classList.add('active');
							storeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
							changeMarkerIcon(marker);
						}
					});


					listStore.appendChild(storeItem);
				});

				if (storesInRadius.length === 0) {
					storeFilters.style.display = 'none';
					noStore.style.display = 'block';
					map.setZoom(14);
				} else {
					storeFilters.style.display = 'block';
					noStore.style.display = 'none';
				}

				storeFilters.querySelector('span').textContent = storesInRadius.length;

				try {
					const geocoder = new google.maps.Geocoder();
					const address = document.querySelector('.search-input').value;
					geocoder.geocode({ address: addressForGeocoder(address) }, async (results, status) => {
					if (status === 'OK' && results[0]) {
						const ordinatiA = {
							"country": 1,
							"administrative_area_level_1": 2,
							"administrative_area_level_2": 3,
							"administrative_area_level_3": 4,
							"administrative_area_level_4": 5,
							"administrative_area_level_5": 6,
							"locality": 7,
							"route": 8,
							"postal_code": 9
						};
						const addressComponents = results[0].address_components;
						const country = addressComponents.find(component => component.types.includes("country"))?.short_name || 'N/A';
						const province = addressComponents.find(component => component.types.includes("administrative_area_level_2"))?.short_name || 'N/A';
						const city = addressComponents.find(component => component.types.includes("administrative_area_level_3"))?.short_name || 'N/A';
						const areac = addressComponents.find(component => component.types.includes("administrative_area_level_1"))?.short_name || 'N/A';

						var tipo = ''; var shortn = '';
						for (var i = addressComponents.length - 1; i >= 0; i--) {
							var componente = addressComponents[i];

							for (var j = 0; j < componente.types.length; j++) {
								if (ordinatiA[componente.types[j]]) {
									tipo = componente.types[j];
									shortn = componente.short_name;
									break;
								}
							}
						}
						// console.log('tipo ', tipo, ' - short 2 ', shortn);

						const agents = await fetchAgentsData(tipo, country, province, city, areac, shortn);




						if (agents.length > 0) {
							agents.forEach(agent => {
								const agentItem = document.createElement('li');
								agentItem.classList.add('agent');

								var nomeContenuto = agent.nameWebRanking.replace(/'/g, "`");
								var prov = window.data.provinciaGA4;
								if (!prov || window.data.searchByTextBox)
									nomeContenuto += "_-";


								agentItem.innerHTML = `
                            <div class="tagAgent" >${window.storeLocatorSettings.localizedStrings.LBL_AGENTE_ZONA}</div>
                            <div class="store-header" >
                                <div class="wrap">
                                    <div class="storeName">${agent.name}</div>
                                </div>
                            </div>
                            <div class="store-body" >
                                <div class="wrap">
                                    <div class="item-info">
                                        <ul>
                                            <li>
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M11.708 12.4789C10.4052 13.7817 7.06855 12.5575 4.25551 9.74449C1.44248 6.93145 0.218261 3.59485 1.52114 2.29197L2.37898 1.43412C2.9712 0.841904 3.94703 0.857558 4.55856 1.46909L5.88728 2.79781C6.49881 3.40934 6.51447 4.38517 5.92225 4.97739L5.73799 5.16165C5.41825 5.48139 5.38696 5.99721 5.68407 6.35725C5.97064 6.70452 6.2796 7.05043 6.61458 7.38542C6.94957 7.7204 7.29548 8.02936 7.64275 8.31593C8.00279 8.61304 8.51861 8.58175 8.83835 8.26201L9.02261 8.07775C9.61483 7.48553 10.5907 7.50119 11.2022 8.11272L12.5309 9.44144C13.1424 10.053 13.1581 11.0288 12.5659 11.621L11.708 12.4789Z" stroke="#2B3F6C"/>
                                                </svg>
                                                <a onclick="storeLocatorInteractions('listing', 'get_call', '${nomeContenuto}');agenteSelezionato(${agent.id},'${agent.name4GA}','','agente','','${agent.country}','tel_card')" href="tel:${agent.phone}">${agent.phone}</a>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="item-cta">
                                        <div>
                                            <a href="javascript:;" onclick="storeLocatorInteractions('listing', 'contact_store', '${nomeContenuto}');agenteSelezionato(${agent.id},'${agent.name4GA}','','agente','','${agent.country}','contact_card')" class="btn btn--brown linkFancyAgent" data-agent="${encodeURIComponent(JSON.stringify(agent))}">${window.storeLocatorSettings.localizedStrings.LBL_CONTATTA_AGENTE}</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
								listAgent.appendChild(agentItem);
							});
						}

						await triggerGA4Event(); // Trigger GA4 event after fetching agents
							scriviListaGA4(storesInRadius,agents);

					}
				});
				} catch (geocoderError) {
					console.warn('Geocoder error:', geocoderError);
				}

				const bounds = new google.maps.LatLngBounds();
				markers.forEach(marker => bounds.extend(marker.getPosition()));
				if (markers.length > 0) {
					map.fitBounds(bounds);
				}

				loader.style.display = 'none';

				const mapSection = document.querySelector('.section--map-store-locator');
				if (mapSection) {
					const headerHeight = 100;
					const scrollPosition = mapSection.offsetTop - headerHeight;
					window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
				}
			} catch (error) {
				console.error('Error performing search:', error);
			} finally {
				searchExecuted = false; // Resetta la variabile di stato per consentire nuove ricerche in futuro
				// console.log("Search completed. Search executed reset to false.");

			}
		}

		const searchInputs = document.querySelectorAll('.search-input');
		searchInputs.forEach(input => {
			input.addEventListener('input', (e) => {
				searchInputs.forEach(syncInput => {
					if (syncInput !== e.target) {
						syncInput.value = e.target.value;
					}
				});
			});
		});

		// Evento di click sul pulsante di ricerca
		document.querySelectorAll('.btn--search').forEach(button => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				window.data.searchByCTA = true; // Imposta il flag per indicare che la ricerca è stata avviata tramite la search box
				window.data.searchByTextBox = false; // Resetta il flag di ricerca tramite textbox
				console.log("1105 Search button clicked. searchByCTA set to true.");
				search();
			});
		});

		// Evento per la pressione di Invio sul campo di input
		document.querySelectorAll('.search-input').forEach(input => {
			input.addEventListener('keydown', function (e) {
				if (e.key === 'Enter') {
				const autocompleteContainer = document.querySelector('.pac-container');
				const hasAutocompleteItems = autocompleteContainer && autocompleteContainer.querySelector('.pac-item');
				if (autocomplete && hasAutocompleteItems) {
					// Lascia che Google Autocomplete gestisca la selezione con Invio
						search();
					}
				}
			});
		});

		document.querySelectorAll('.btn--myPosition').forEach(button => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				const locale = detectLocale();
				const { country: siteCountry } = getCountryAndCenter(locale);
				if (navigator.geolocation) {
					navigator.geolocation.getCurrentPosition(async (position) => {
						try {
							const location = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
							const geocoder = new google.maps.Geocoder();
							geocoder.geocode({ location }, async (results, status) => {
								if (status === 'OK' && results[0]) {

								var prov = window.data.provinciaGA4;

								if (window.data.provinciaGA4 == "" || window.data.searchByTextBox)
									prov = "-";

								storeLocatorFindPosition(prov, results[0].formatted_address);

								const userCountry = results[0].address_components.find(component => component.types.includes("country")).short_name;
								if (userCountry === siteCountry) {
									searchInputs.forEach(input => {
										input.value = results[0].formatted_address;
									});

									// Aggiorna le coordinate di ricerca con quelle dell'utente
									searchLocationLat = position.coords.latitude;
									searchLocationLng = position.coords.longitude;
									// console.log(`Updated coordinates from My Position: Lat ${searchLocationLat}, Lng ${searchLocationLng}`);

									await performSearch(location, results);
								} else {
									clearMarkers();  // Rimuove i marker se i paesi non corrispondono
									map.setCenter(location); // Centra la mappa sulla posizione dell'utente
									map.setZoom(10); // Regola il livello di zoom
								}
							}
						});
						} catch (geocoderError) {
							console.warn('Geocoder error in My Position:', geocoderError);
							alert('Unable to determine your location. Please try again.');
						}
					}, (error) => {
						alert('Errore nella geolocalizzazione: ' + error.message);
					});
				} else {
					alert('La geolocalizzazione non è supportata dal tuo browser.');
				}
			});
		});

		document.querySelector('.listStore').addEventListener('click', (e) => {
			if (e.target.closest('.wrap-today')) {
				toggleWeekActive(e);
			}
		});

		document.querySelector('.listStore').addEventListener('keydown', (e) => {
			if ((e.key === 'Enter' || e.keyCode === 13) && e.target.closest('.wrap-today')) {
				e.preventDefault();
				toggleWeekActive(e);
			}
		});

		const province = window.data.provincia;// getProvinceFromUrl();
		if (province) {
			const searchInput = document.querySelector('.search-input');
			searchInput.value = province;
			const event = new Event('input', { bubbles: true });
			searchInput.dispatchEvent(event);

			const searchButton = document.querySelector('.btn--search');
			search(true);
		}

		// Scorre tutte le province per gestire l'evento GA4
		var provList = $('ul.provList>li a');
		$.each(provList, function (index, node) {
			$(this).on("click", function () {
				window.data.provincia= $(this).data("title"); // Imposta la provincia cliccata per GA4
				storeLocatorProvince($(this).data("title"));
			});
		})


		document.querySelector('.btn--contattaci').addEventListener('click', (e) => {

			var prov = "-";
			if (window.data.provinciaGA4 && !window.data.searchByTextBox)
				prov = window.data.provinciaGA4;

			storeLocatorInteractions('store', 'richiedi_appuntamento', prov);
		});

		// Funzione per l'evento GA4
		async function triggerGA4Event() {
			console.log("TRigger GA4 event function called. triggerExecuted:", triggerExecuted);
										if (triggerExecuted) return;

										triggerExecuted = true;
										try {
											const address = document.querySelector('.search-input').value;
											const prov = window.data.provinciaGA4 || "-";

											var position = "search_bar";
											if (!window.data.searchByTextBox && prov != "-")
												position = "listing_province";

											console.log("1227 triggerGA4Event - address:", address, "prov:", prov, "position:", position, "  window.data.searchByTextBox:", window.data.searchByTextBox, " window.data.searchByCTA:", window.data.searchByCTA);

											if (window.data.searchByTextBox || window.data.searchByCTA) {
												storeLocatorSearch(address, prov, position);

												//window.data.searchByTextBox = true;
										//		window.data.searchByCTA = false;
											}

										} catch (error) {
											console.error('Error triggerGA4 search:', error);
										} finally {
											triggerExecuted = false; // Resetta la variabile di stato per consentire nuove ricerche in futuro

										}

									}

		function search(fromProv) {
			if (fromProv)
				window.data.searchByTextBox = false;
			else
				window.data.searchByTextBox = true;

			const address = document.querySelector('.search-input').value;
			if (!address) return;
			const locale = detectLocale();
			const { country } = getCountryAndCenter(locale);
			if (address) {
				if (!skipGA4Event) {
					var prov = window.data.provinciaGA4;
					var label = prov && fromProv ? prov : "-";

					if (typeof fromProv === 'undefined' && prov)
						label = address;

					var position = "search_bar";
					if (!window.data.searchByTextBox && fromProv)
						position = "listing_province";

					if (fromProv) {
						//storeLocatorProvince(prov);

					} else {
						console.log("1266 search - address:", address, "prov:", prov, "position:", position, "  window.data.searchByTextBox:", window.data.searchByTextBox,
							" window.data.searchByCTA:", window.data.searchByCTA," window.data.provincia:", window.data.provincia);

							/*if ((!window.data.searchByTextBox || !window.data.searchByCTA) || fromProv) {
							//storeLocatorSearch(address, label, position);
							window.data.searchByTextBox = false;
							window.data.searchByCTA = false;
						} */

					}
				}

				skipGA4Event = false; // Resetting flag after GA4 event
				try {
					const geocoder = new google.maps.Geocoder();
					geocoder.geocode({ address: addressForGeocoder(address) }, async (results, status) => {
						if (status === 'OK') {
							const location = results[0].geometry.location;
							searchLocationLat = location.lat();
							searchLocationLng = location.lng();
							await performSearch(location, results);
						}
					});
				} catch (geocoderError) {
					console.warn('Geocoder error in search:', geocoderError);
				}
				skipGA4Event = false; // Reset flag after search
			}
		}
	}

	function openAgentForm(agentID) {
		const agentForm = document.getElementById('agent-form');
		agentForm.setAttribute('data-agent-id', agentID);
	}

	async function onSubmitAgent() {
		const form = document.getElementById('form-agente');
		const formData = new FormData(form);
		const recaptchaToken = grecaptcha.getResponse();

		const typeContact = formData.get('TypeContact') || 'agent';
		const agentID = formData.get('agentID') || '0';
		const storeID = formData.get('storeID') || '0';

		formData.set('TypeContact', typeContact);
		formData.set('agentID', agentID);
		formData.set('storeID', storeID);

		const model = {
			Captcha: recaptchaToken,
			Nome: formData.get('Nome') || '',
			Cognome: formData.get('Cognome') || '',
			Email: formData.get('Email') || '',
			Nazione: formData.get('Nazione') || '0',
			Citta: formData.get('Citta') || '',
			Country: formData.get('Country') || '',
			Messaggio: formData.get('Messaggio') || '',
			Privacy: formData.get('Privacy') === 'on',
			Newsletter: formData.get('Newsletter') === 'on',
			TypeContact: formData.get('TypeContact') || '',
			agentID: formData.get('agentID') || '0',
			storeID: formData.get('storeID') || '0'
		};

		const data = {
			data: model
		};

		// console.log('Model:', model);

		try {
			const response = await fetch('/WS/wsUsers.asmx/ContactStore', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(data)
			});
			
			//if (!response.ok) {
			//	throw new Error('Network response was not ok ' + response.statusText);
			//}

			const responseData = await response.json();
			// console.log('Success:', responseData);

			if (responseData.d.Status) {
				document.getElementById('thank-you-message').style.display = 'block';
				document.getElementById('form-agente').style.display = 'none';
				document.getElementById('storeInfo').style.display = 'none';
				document.getElementById('titleModal').style.display = 'none';

				var storeAgentName = responseData.d.Props.StoreAgentName.replace(/'/g, "`");

				var prov = window.data.provinciaGA4;
				if (prov && !window.data.searchByTextBox)
					storeAgentName += "_" + prov;
				else
					storeAgentName += "_-";

				// Gestione evento GA
				storeLocatorRequestSent(storeAgentName);
			}
			else {
				alert(responseData.d.HTML);
			}

		} catch (error) {
			console.log('Error:', error);
			console.error('There has been a problem with your fetch operation:', error);
			alert('There was an error submitting the form. Please try again later.');
		}
	}

	function resetForm() {
		document.getElementById('form-agente').reset();
		const form = document.getElementById('form-agente');
		const titleElement = document.getElementById('titleModal');
		document.querySelector('#agent-form .store-info').innerHTML = '';
		if (form) {
			const typeInput = form.querySelector('input[name="TypeContact"]');
			const agentIdInput = form.querySelector('input[name="agentID"]');
			const storeIdInput = form.querySelector('input[name="storeID"]');

			if (typeInput) {
				typeInput.value = 'agent';
			}

			if (agentIdInput) {
				agentIdInput.value = '';
			}

			if (storeIdInput) {
				storeIdInput.value = '';
			}
		}

		if (titleElement) {
			titleElement.textContent = window.storeLocatorSettings.localizedStrings.LBL_CONTATTA_AGENTE;
		}
		document.getElementById('thank-you-message').style.display = 'none';
		document.getElementById('form-agente').style.display = 'block';
		document.getElementById('storeInfo').style.display = 'block';
		document.getElementById('titleModal').style.display = 'block';
	}

	function toggleWrapMap() {
		const wrapMap = document.querySelector('.wrap-map');
		const mapToggle = document.querySelector('.showMap input[type="checkbox"]');

		mapToggle.addEventListener('change', function () {
			if (mapToggle.checked) {
				wrapMap.style.display = 'block';
			} else {
				wrapMap.style.display = 'none';
			}
		});
	}

	function scrollToMap() {
		const btnTop = document.querySelector('.btn-top');
		if (btnTop) {
			btnTop.addEventListener('click', (e) => {
				e.preventDefault();
				const wrapMap = document.querySelector('.wrap-map');
				if (wrapMap) {
					wrapMap.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
				btnTop.classList.remove('visible');
			});
		}
	}

	function observeMapVisibility() {
		const listStore = document.querySelector('.listStore');
		const btnTop = document.querySelector('.btn-top');

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					// Se listStore è visibile, mostra il pulsante btn-top
					btnTop.style.display = 'block';
				} else {
					// Se listStore non è visibile, nascondi il pulsante btn-top
					btnTop.style.display = 'none';
				}
			});
		}, {
			root: null,
			threshold: 0.1 // Attiva quando almeno il 10% di listStore è visibile
		});

		// Osserva l'elemento listStore
		if (listStore) {
			observer.observe(listStore);
		}
	}

	function addressForGeocoder(address) {
		let addressToSearch = address;

		// La mappa per Cotto d'este è impostata sempre su lingua EN.
		// Questo impedisce di trovare risultati quando si cerca solo il CAP.
		//
		// Questa modifica quando sto in Italiano e cerco una sola parola
		// aggiunge "Italy" alla ricerca.
		// (Aiuta per la ricerca del cap, ma dovebbre essere utile anche per i nomi di città)
		if (!(addressToSearch + "").match(" ")) {
			const locale = detectLocale();
			const { country } = getCountryAndCenter(locale);
			if (country === "IT")
				addressToSearch += ", Italy";
		}

		return addressToSearch;
	}
	$(document).ready(function () {
		this.labels = window.labels || {
			approximately: window.storeLocatorSettings.localizedStrings.LBL_DISTANZA_STORE,
			raggiungiStore: window.storeLocatorSettings.localizedStrings.LBL_RAGGIUNGERE_STORE,
			contactStore: window.storeLocatorSettings.localizedStrings.LBL_CONTATTA_STORE,
			contactAgent: window.storeLocatorSettings.localizedStrings.LBL_CONTATTA_AGENTE,
			agenteZona: window.storeLocatorSettings.localizedStrings.LBL_AGENTE_ZONA
		};
		$(document).on('click', '.linkFancyStore', function () {
			const encodedStoreData = $(this).data('store');
			if (!encodedStoreData) {
				return;
			}

			const storeData = JSON.parse(decodeURIComponent(encodedStoreData));
			const storeInfo = `
				<div class="store-name">${storeData.name}</div>
				<div class="store-phone">
					<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M11.708 12.4789C10.4052 13.7817 7.06855 12.5575 4.25551 9.74449C1.44248 6.93145 0.218261 3.59485 1.52114 2.29197L2.37898 1.43412C2.9712 0.841904 3.94703 0.857558 4.55856 1.46909L5.88728 2.79781C6.49881 3.40934 6.51447 4.38517 5.92225 4.97739L5.73799 5.16165C5.41825 5.48139 5.38696 5.99721 5.68407 6.35725C5.97064 6.70452 6.2796 7.05043 6.61458 7.38542C6.94957 7.7204 7.29548 8.02936 7.64275 8.31593C8.00279 8.61304 8.51861 8.58175 8.83835 8.26201L9.02261 8.07775C9.61483 7.48553 10.5907 7.50119 11.2022 8.11272L12.5309 9.44144C13.1424 10.053 13.1581 11.0288 12.5659 11.621L11.708 12.4789Z" stroke="#2B3F6C"/>
					</svg>
					<a href="tel:${storeData.phone || ''}"
						class="dtEventSend"
						data-gtm-eventname="store_locator_interactions"
						data-gtm-position="mappa"
						data-gtm-label="get_call"
						data-gtm-nomecontenuto="${storeData.nameWebRanking || ''}"
					>${storeData.phone || ''}</a>
				</div>
			`;

			$('#agent-form .store-info').html(storeInfo);
			$('#form-agente').find('input[name="TypeContact"]').val('store');
			$('#form-agente').find('input[name="storeID"]').val(storeData.id);
			$('#form-agente').find('input[name="agentID"]').val('');
			$('#titleModal').text(window.storeLocatorSettings.localizedStrings.LBL_CONTATTA_STORE);

			$.fancybox.open({
				src: '#agent-form',
				type: 'inline',
				opts: {
					touch: false,
					afterClose: function () {
						resetForm();
					},
				}
			});
		});

		$(document).on('click', '.linkFancyAgent', function () {
			const encodedAgentData = $(this).data('agent');
			if (!encodedAgentData) {
				return;
			}

			const agentData = JSON.parse(decodeURIComponent(encodedAgentData));

			const agentInfo = `
				<div class="tagAgent">${window.storeLocatorSettings.localizedStrings.LBL_AGENTE_ZONA}</div>
				<div class="store-name">${agentData.name}</div>
				<div class="store-phone">
					<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M11.708 12.4789C10.4052 13.7817 7.06855 12.5575 4.25551 9.74449C1.44248 6.93145 0.218261 3.59485 1.52114 2.29197L2.37898 1.43412C2.9712 0.841904 3.94703 0.857558 4.55856 1.46909L5.88728 2.79781C6.49881 3.40934 6.51447 4.38517 5.92225 4.97739L5.73799 5.16165C5.41825 5.48139 5.38696 5.99721 5.68407 6.35725C5.97064 6.70452 6.2796 7.05043 6.61458 7.38542C6.94957 7.7204 7.29548 8.02936 7.64275 8.31593C8.00279 8.61304 8.51861 8.58175 8.83835 8.26201L9.02261 8.07775C9.61483 7.48553 10.5907 7.50119 11.2022 8.11272L12.5309 9.44144C13.1424 10.053 13.1581 11.0288 12.5659 11.621L11.708 12.4789Z" stroke="#2B3F6C"/>
					</svg>
					<a href="tel:${agentData.phone}"
						class="dtEventSend"
						data-gtm-eventname="store_locator_interactions"
						data-gtm-position="mappa"
						data-gtm-label="get_call"
						data-gtm-nomecontenuto="${agentData.nameWebRanking}"
					>${agentData.phone}</a>
				</div>
			`;

			$('#agent-form .store-info').html(agentInfo);
			$('#form-agente').find('input[name="TypeContact"]').val('agent');
			$('#form-agente').find('input[name="agentID"]').val(agentData.id);
			$('#form-agente').find('input[name="storeID"]').val('');
			$('#titleModal').text(window.storeLocatorSettings.localizedStrings.LBL_CONTATTA_AGENTE);

			$.fancybox.open({
				src: '#agent-form',
				type: 'inline',
				opts: {
					touch: false,
					afterClose: function () {
						resetForm();
					},
				}
			});
		});

		$(document).on('click', '.close-fancybox', function (event) {
			event.preventDefault();
			$.fancybox.close();
		});

		$('#Nazione').on('change', function () {
			if (this.value == 204) {

				document.getElementById('divCountry').style.display = 'block';
			} else {
				document.getElementById('divCountry').style.display = 'none';
			}
		});

		$('#form-agente').on('submit', function (event) {
			event.preventDefault();
			onSubmitAgent();
		});
	});

	document.addEventListener('DOMContentLoaded', async function () {
		try {
			await loadGoogleMapsAPI();
			// Attende un frame aggiuntivo per essere sicuri che tutti i moduli siano caricati
			await new Promise(resolve => setTimeout(resolve, 100));
			await initMap();
		} catch (error) {
			console.error('Error loading Google Maps:', error);
		}
		toggleWrapMap();
		scrollToMap();
		observeMapVisibility();
	});

	window.initMap = initMap;
}
